import React, { memo, useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { Scrollbars } from "react-custom-scrollbars";

import {
  formatNumber,
  formatNumberPS,
  formatNumber_N1,
  formatPrice,
  formatPrice_N1,
  priceToNumber,
} from "../../helpers/utils";

import { getFromApiAsync } from "../../helpers/apiHelper";

export default memo(
  function StockMatchDetailV2(props) {
    const { t } = useTranslation();
    const [stockMatches, setStockMatches] = useState([]);
    const [counter, setCounter] = useState(0);
    const [price, setPrice] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const { code, isDerivative } = props;

    const refFirstTicks = useRef("0");
    const refLastTicks = useRef("0");
    const refCode = useRef(null);
    const refScrollBody = useRef(null);

    const _refPrice = priceToNumber(price.refPrice) * 1000;
    const _floorPrice = priceToNumber(price.floorPrice) * 1000;
    const _ceilPrice = priceToNumber(price.ceilPrice) * 1000;

    const _getMatchDetails = (searchCode) => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      let _url = "pr/stockmatch/" + searchCode + "?lastTicks=" + refLastTicks.current + "&backward=Y";
      if (refLastTicks.current > 0) {
        _url = "pr/stockmatch/" + searchCode + "?lastTicks=" + refLastTicks.current + "&backward=N";
      }

      getFromApiAsync(_url)
        .then((res) => {
          if (searchCode === refCode.current) {
            try {
              if (res.data && res.data.length > 0) {
                setStockMatches((prevState) =>
                  [...prevState, ...res.data].sort((a, b) =>
                    b.ticks.localeCompare(a.ticks)
                  )
                );
                //
                const _firstTicks = res.data.sort((a, b) =>
                  a.ticks.localeCompare(b.ticks)
                )[0].ticks;
                const _lastTicks = res.data.sort((a, b) =>
                  b.ticks.localeCompare(a.ticks)
                )[0].ticks;

                if (
                  (refLastTicks.current ?? "0") === "0" ||
                  _lastTicks.localeCompare(refLastTicks.current) > 0
                ) {
                  refLastTicks.current = _lastTicks;
                }

                if (
                  (refFirstTicks.current ?? "0") === "0" ||
                  _firstTicks.localeCompare(refFirstTicks.current) < 0
                ) {
                  refFirstTicks.current = _firstTicks;
                }
              }
            } catch (error) {
              console.log("error", error);
            }
            setIsLoading(false);
          } else {
            console.log(searchCode, refCode.current);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          console.log("catch", err);
        });
    };

    const _handleScroll = ({ target }) => {
      const { scrollTop, scrollLeft, scrollHeight, clientHeight } = target;

      if (clientHeight + scrollTop >= scrollHeight - 1) {
        _getMatchDetails(code);
      }

      if (refScrollBody.current) {
        refScrollBody.current.scrollTo(scrollTop);
      }
    };

    //
    useEffect(() => {
      if (refCode.current != code) {
        refFirstTicks.current = "0";
        refLastTicks.current = "0";
        setIsLoading(false);
        setStockMatches([]);
        setPrice({
          floorPrice: props.floorPrice,
          refPrice: props.refPrice,
          ceilPrice: props.ceilPrice,
        });
      }
      refCode.current = code;
      const interval = setInterval(() => {
        _getMatchDetails(code);
      }, 1000);

      // Hủy interval khi component bị unmount
      return () => clearInterval(interval);

    }, [code]);

    //
    return (
      <div className={props.className} style={props.style}>
        <div className="d-flex flex-column h-100 side-tab-table">
          <div className="prc-row-head">
            <div className={"prc-col4 justify-content-start pdl-8"}>
              {t("thoiGian")}
            </div>
            <div className={"prc-col2 justify-content-end"}>{t("gia")}</div>
            <div className={"prc-col2 justify-content-end"}>+/-</div>
            <div className={"prc-col3 justify-content-end pdr-8"}>
              {t("kl")}
            </div>
            {
              isDerivative
              &&
              (<div className={"prc-col3 justify-content-end pdr-8"}>
                {t("kltichluy")}
              </div>)
            }
          </div>
          <div className="flex-1" style={{ position: "relative" }}>
            {isLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  zIndex: 1,
                  padding: 3,
                  backgroundColor: "rgba(0,0,0,0.5)",
                }}
              >
                {/* trungdh ẩn đi vì call api liên tục */}
                {/* Loading... */}
              </div>
            )}
            <AutoSizer>
              {({ height, width }) => {
                return (
                  <Scrollbars
                    autoHide={true}
                    autoHeight={true}
                    autoHeightMax={height}
                    onScroll={_handleScroll}
                    style={{ width: width }}
                    renderTrackHorizontal={(props) => (
                      <div {...props} className="scroll-track-horizontal" />
                    )}
                    renderTrackVertical={(props) => (
                      <div {...props} className="scroll-track-vertical" />
                    )}
                    renderThumbHorizontal={(props) => (
                      <div {...props} className="scroll-thumb-horizontal" />
                    )}
                    renderThumbVertical={(props) => (
                      <div {...props} className="scroll-thumb-vertical" />
                    )}
                    renderView={(props) => (
                      <div {...props} className="scroll-view" />
                    )}
                    className={"scroll-container"}
                  >
                    <FixedSizeList
                      ref={refScrollBody}
                      height={height}
                      width={"100%"}
                      itemCount={stockMatches.length}
                      itemSize={28}
                      style={{ overflow: false }}
                    >
                      {({ index, style }) => {
                        const _stockMatch = stockMatches[index];
                        let _stockMatchTime = _stockMatch.tradeTime;
                        if (_stockMatch.tradeTime.indexOf(".")) {
                          _stockMatchTime = _stockMatch.tradeTime.substring(0, _stockMatch.tradeTime.indexOf("."));
                        }
                        let classClr = "";
                        if (_stockMatch.matchPrice == _refPrice) {
                          classClr = "clr-ref";
                        } else if (_stockMatch.matchPrice >= _ceilPrice) {
                          classClr = "clr-ceil";
                        } else if (_stockMatch.matchPrice <= _floorPrice) {
                          classClr = "clr-floor";
                        } else if (_stockMatch.matchPrice < _refPrice) {
                          classClr = "clr-down";
                        } else if (_stockMatch.matchPrice > _refPrice) {
                          classClr = "clr-up";
                        }

                        if (isDerivative && _stockMatch.changePrice > 0) {
                          classClr = "clr-up";
                        } else if (isDerivative && _stockMatch.changePrice < 0) {
                          classClr = "clr-down";
                        }

                        if (isDerivative && index > 0) {
                          const _stockMatch_pre = stockMatches[index - 1];

                          if (_stockMatch.matchPrice >= _stockMatch_pre.matchPrice) {
                            classClr = "clr-up";
                          } else {
                            classClr = "clr-down";
                          }
                        }
                        // 
                        return (
                          <div
                            className={clsx(
                              "prc-row",
                              index % 2 === 0 ? "odd" : "even"
                            )}
                            style={style}
                          >
                            <div
                              className={clsx(
                                "prc-col4 text-left justify-content-start pdl-8"
                              )}
                            >
                              {_stockMatchTime}
                            </div>
                            <div
                              className={clsx("prc-col2 text-right", classClr)}
                            >
                              {isDerivative ? formatNumberPS(_stockMatch.matchPrice) : formatPrice(_stockMatch.matchPrice)}
                            </div>
                            <div className={clsx("prc-col2 text-right", classClr)} >
                              {isDerivative ? (_stockMatch.changePrice) : formatPrice_N1(_stockMatch.changePrice)}
                            </div>
                            <div className={"prc-col3 text-right pdr-8"}>
                              {/* 20/03/2025 longdh nhận y/c ko / 1000 cho KL trên bảng giá */}
                              {isDerivative ? formatNumberPS(_stockMatch.change_TotalTradeQtty) : formatNumber(_stockMatch.matchQtty)}
                            </div>
                            {
                              isDerivative
                              &&
                              (
                                <div className={"prc-col3 text-right pdr-8"}>
                                  {formatNumber(_stockMatch.totalTradeQtty)}
                                </div>
                              )
                            }
                          </div>
                        );
                      }}
                    </FixedSizeList>
                  </Scrollbars>
                );
              }}
            </AutoSizer>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.code === nextProps.code) {
      return true; // props are equal
    }
    return false; // props are not equal -> update the component
  }
);
